import { Link } from 'react-router-dom';
import { useThemeStore } from '../../stores/theme.store';
import { Sun, Moon, Icon } from '../../components/ui/Icon';
import { GromlyWordmark } from '../../components/brand/GromlyMark';

interface LegalLayoutProps {
  title: string;
  subtitle: string;
  lastUpdated: string;
  relatedLink: { to: string; label: string };
  children: React.ReactNode;
}

export default function LegalLayout({ title, subtitle, lastUpdated, relatedLink, children }: LegalLayoutProps) {
  const { isDark, toggle } = useThemeStore();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Topbar */}
      <header className="sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200/70 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
            <GromlyWordmark size={24} dark={isDark} />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to={relatedLink.to}
              className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
            >
              {relatedLink.label}
            </Link>
            <button
              onClick={toggle}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              {isDark ? <Icon as={Sun} size={15} /> : <Icon as={Moon} size={15} />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Page header */}
        <div className="mb-10">
          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-teal-600 dark:text-teal-400 mb-3">
            Legal
          </p>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50 mb-2">{title}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="space-y-3">
          {children}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-slate-400 dark:text-slate-500">
            © {new Date().getFullYear()} Gromly · VESTIGI PLATFORM, S.L.
          </p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/privacy" className="text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Terms of Service</Link>
            <Link to="/legal" className="text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Legal Notice</Link>
            <a href="mailto:contact@gromly.io" className="text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
        <span className="font-mono text-xs text-slate-400 dark:text-slate-500 w-6 shrink-0">{number}</span>
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      </div>
      <div className="px-5 py-4 space-y-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

export function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 text-sm font-mono space-y-1">
      {children}
    </div>
  );
}

export function DataTable({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className={`px-5 py-3 align-top ${j === 0 ? 'font-medium text-slate-700 dark:text-slate-300' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
